import React, { useEffect, useState } from 'react'
import { useFurnizorPage } from './hook/useFurnizorPage'
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tag, Button, Typography, Space, Divider, Row, Col, Avatar, Descriptions, Badge, Progress, Dropdown } from 'antd';
import { ShopOutlined, PhoneOutlined, MailOutlined, DollarOutlined, DownOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useStateContext } from '../../../../../contexts/ContextProvider';
import FurnizorPDFGenerator from './components/FurnizorPDFGenerator';

const { Title, Text } = Typography;

function FurnizorPage() {
  const { 
    furnizorProducts, 
    furnizorData, 
    loading, 
    productRequestStatuses, 
    purchaseRequestsStatuses, 
    getFurnizorProducts, 
    getFurnizorData, 
    getProductRequestStatuses, 
    getPurchaseRequestsStatuses,
    changeProductRequestStatus 
  } = useFurnizorPage();

  const navigate = useNavigate();

  const { user } = useStateContext();
  
  const { furnizor_id } = useParams();
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    getFurnizorProducts({furnizor_id: furnizor_id});
    getFurnizorData({furnizor_id: furnizor_id});
    getProductRequestStatuses();
    getPurchaseRequestsStatuses();
  }, []);

  // Filtrează produsele bazat pe rolul utilizatorului
  const getFilteredProducts = () => {
    // Dacă role_id este 5, nu afișăm produsele cu status 1 (Ciornă)
    if (user?.role_id === 5) {
      return furnizorProducts.filter(product => product.status_id !== 1);
    }
    // Pentru restul rolurilor, afișăm toate produsele
    return furnizorProducts;
  };

  // Grupează produsele după purchase_request_id (folosind produsele filtrate)
  const groupedProducts = getFilteredProducts().reduce((acc, product) => {
    const requestId = product.purchase_request_id;
    if (!acc[requestId]) {
      acc[requestId] = [];
    }
    acc[requestId].push(product);
    return acc;
  }, {});

  // Găsește statusul pentru un produs
  const getProductStatus = (statusId) => {
    return productRequestStatuses.find(status => status.id === statusId);
  };

  // Calculează totalul pentru o cerere
  const calculateRequestTotal = (products) => {
    return products.reduce((total, product) => {
      return total + (product.produs_oferta.bought_price * product.produs_oferta.product_qt);
    }, 0);
  };

  // Calculează sumar pentru o cerere (similar cu DecideFurnizori)
  const calculateRequestSummary = (products) => {
    const totalProducts = products.length;
    const statusCounts = products.reduce((acc, product) => {
      const status = getProductStatus(product.status_id);
      if (status) {
        acc[status.id] = (acc[status.id] || 0) + 1;
      }
      return acc;
    }, {});

    // Calculează progresul bazat pe statusuri
    const draftCount = statusCounts[1] || 0; // Ciornă
    const orderedCount = statusCounts[2] || 0; // Comandat
    const acknowledgedCount = statusCounts[3] || 0; // Luat în lucru
    const awaitingPaymentCount = statusCounts[4] || 0; // Trebuie să-l plătim
    const paidCount = statusCounts[5] || 0; // Plătit
    const readyCount = statusCounts[6] || 0; // Gata de ridicare
    const closedCount = statusCounts[7] || 0; // Închis

    const processedProducts = totalProducts - draftCount; // Produse care au trecut de stadiul de ciornă
    const completedProducts = readyCount + closedCount; // Produse finalizate

    const processProgress = totalProducts > 0 ? (processedProducts / totalProducts) * 100 : 0;
    const completionProgress = processedProducts > 0 ? (completedProducts / processedProducts) * 100 : 0;

    return {
      totalProducts,
      statusCounts,
      processedProducts,
      completedProducts,
      processProgress,
      completionProgress,
      draftCount,
      orderedCount,
      acknowledgedCount,
      awaitingPaymentCount,
      paidCount,
      readyCount,
      closedCount
    };
  };

  // Schimbă statusul unui produs
  const handleStatusChange = async (productId, newStatusId) => {
    await changeProductRequestStatus({
      pr_id: productId,
      pr_status_id: newStatusId
    });
    // Reîncarcă datele
    getFurnizorProducts({furnizor_id: furnizor_id});
  };

  // Schimbă statusul tuturor produselor dintr-o cerere
  const handleBulkStatusChange = async (requestId, newStatusId) => {
    const requestProducts = groupedProducts[requestId];
    
    // Schimbă statusul pentru toate produsele din cerere
    for (const product of requestProducts) {
      await changeProductRequestStatus({
        pr_id: product.id,
        pr_status_id: newStatusId
      });
    }
    
    // Reîncarcă datele
    getFurnizorProducts({furnizor_id: furnizor_id});
  };

  // Filtrează statusurile disponibile bazat pe rol
  const getAvailableStatuses = () => {
    if (user?.role_id === 5) {
      // Pentru role_id 5, ascund primul (1 - Ciornă) și ultimul (7 - Închis) status
      return productRequestStatuses.filter(status => status.id !== 1 && status.id !== 7);
    }
    return productRequestStatuses;
  };

  // Componentă pentru progress bar cerere
  const RequestSummary = ({ summary }) => {
    // Calculează progresul pentru status 6 (gata de ridicare)
    const readyProgress = summary.totalProducts > 0 ? (summary.readyCount / summary.totalProducts) * 100 : 0;
    
    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }} className='my-3'>
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>
              Gata de ridicare: {summary.readyCount}/{summary.totalProducts}
            </Text>
          </div>
          <Progress 
            percent={Math.round(readyProgress)} 
            size="small" 
            status={readyProgress === 100 ? 'success' : 'active'}
            showInfo={false}
            strokeColor="#10B981"
          />
        </div>

        <Space size="small" wrap>
          {summary.draftCount > 0 && (
            <Tag color="#6B7280">Ciornă ({summary.draftCount})</Tag>
          )}
          {summary.orderedCount > 0 && (
            <Tag color="#3B82F6">Comandat ({summary.orderedCount})</Tag>
          )}
          {summary.acknowledgedCount > 0 && (
            <Tag color="#8B5CF6">În lucru ({summary.acknowledgedCount})</Tag>
          )}
          {summary.awaitingPaymentCount > 0 && (
            <Tag color="#F59E0B">De plătit ({summary.awaitingPaymentCount})</Tag>
          )}
          {summary.paidCount > 0 && (
            <Tag color="#059669">Plătit ({summary.paidCount})</Tag>
          )}
          {summary.readyCount > 0 && (
            <Tag color="#10B981">Gata ({summary.readyCount})</Tag>
          )}
          {summary.closedCount > 0 && (
            <Tag color="#374151">Închis ({summary.closedCount})</Tag>
          )}
        </Space>
      </Space>
    );
  };

  return (
    <div style={{ padding: '12px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header cu informații furnizor - Responsive */}
      <Card style={{ marginBottom: '16px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        {/* Mobile: Stack vertical */}
        <div className="block md:hidden">
          <div className="text-center mb-4">
            <div className='flex items-center justify-start mb-2'>
            <Button type="link" onClick={() => navigate(-1)}>
              <ArrowLeftOutlined style={{ fontSize: '24px' }} />
            </Button>
            </div>
            <Title level={3} style={{ margin: 0, color: '#1890ff', fontSize: '18px' }}>
              {furnizorData.denumire || 'Încărcare...'}
            </Title>
            {furnizorData.brand_name && (
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Brand: {furnizorData.brand_name}
              </Text>
            )}
          </div>
          
          {/* Contact info pentru mobile */}
          {furnizorData.contact && furnizorData.contact[0] && (
            <div className="text-center mb-4">
              <Space direction="vertical" size="small">
                <Text><PhoneOutlined /> {furnizorData.contact[0].telefon}</Text>
                <Text><MailOutlined /> {furnizorData.contact[0].email}</Text>
              </Space>
            </div>
          )}
          
          {/* Butonul pentru PDF pe mobile */}
          {user?.role_id !== 5 && (
            <div className="text-center mb-4">
              <FurnizorPDFGenerator 
                furnizorData={furnizorData}
                groupedProducts={groupedProducts}
                productRequestStatuses={productRequestStatuses}
                calculateRequestTotal={calculateRequestTotal}
                furnizor_id={furnizor_id}
              />
            </div>
          )}
        </div>

        {/* Desktop: Layout orizontal */}
        <div className="hidden md:block">
          <Row gutter={24} align="middle">
            <Col>
              <Button type="link" onClick={() => navigate(-1)}>
                <ArrowLeftOutlined style={{ fontSize: '24px' }} />
              </Button>
              <Avatar size={64} style={{ backgroundColor: '#1890ff' }}>
                <ShopOutlined style={{ fontSize: '32px' }} />
              </Avatar>
            </Col>
            <Col flex="auto">
              <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                {furnizorData.denumire || 'Încărcare...'}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                {furnizorData.brand_name && `Brand: ${furnizorData.brand_name}`}
              </Text>
            </Col>
            <Col>
              <Space direction="vertical" size="small">
                {furnizorData.contact && furnizorData.contact[0] && (
                  <>
                    <Text><PhoneOutlined /> {furnizorData.contact[0].telefon}</Text>
                    <Text><MailOutlined /> {furnizorData.contact[0].email}</Text>
                  </>
                )}
                {/* Butonul pentru PDF */}
                <FurnizorPDFGenerator 
                  furnizorData={furnizorData}
                  groupedProducts={groupedProducts}
                  productRequestStatuses={productRequestStatuses}
                  calculateRequestTotal={calculateRequestTotal}
                  furnizor_id={furnizor_id}
                />
              </Space>
            </Col>
          </Row>
        </div>
        
        {furnizorData.id_no && (
          <Divider />
        )}
        
        {/* Informații suplimentare - responsive grid */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Cod fiscal">{furnizorData.id_no}</Descriptions.Item>
              <Descriptions.Item label="Termen plată">{furnizorData.termen_de_plata} zile</Descriptions.Item>
              <Descriptions.Item label="Termen livrare">{furnizorData.termen_de_livrare} zile</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Adresă juridică">
                {furnizorData.adresa_juridica}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={8}>
            {furnizorData.website && (
              <Button type="link" href={furnizorData.website} target="_blank" className="p-0">
                Vizitează website-ul
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      {/* Cereri de achiziție */}
      <Title level={3} style={{ marginBottom: '16px', fontSize: '18px' }}>
        Cereri de Achiziție ({Object.keys(groupedProducts).length})
      </Title>

      {Object.entries(groupedProducts).map(([requestId, products]) => {
        const total = calculateRequestTotal(products);
        const summary = calculateRequestSummary(products);
        const hasUrgentStatus = products.some(p => [4, 6].includes(p.status_id)); // Awaiting payment sau ready for pickup
        
        return (
          <>
            <Card
              key={`${requestId}-mobile`}
              style={{ 
                marginBottom: '16px', 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: hasUrgentStatus ? '2px solid #f59e0b' : '1px solid #d9d9d9'
              }}
              title={
                // Mobile: Layout vertical
                <div className="block md:hidden">
                  <div className="mb-3">
                    <Text strong style={{ fontSize: '16px' }}>
                      {products[0]?.client_name || `Cererea #${requestId}`}
                    </Text>
                  </div>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space wrap>
                      <Badge 
                        count={products.length} 
                        style={{ backgroundColor: '#1890ff' }}
                        title="Numărul de produse"
                      />
                      <Tag color="blue" icon={<DollarOutlined />}>
                        {total.toFixed(2)} MDL
                      </Tag>
                    </Space>
                    <div className="w-full">
                      <RequestSummary summary={summary} />
                    </div>
                    <Space wrap className="w-full justify-between">
                      <Dropdown
                        menu={{
                          items: getAvailableStatuses().map(status => ({
                            key: status.id,
                            label: (
                              <div 
                                style={{ 
                                  padding: '8px 12px',
                                  color: status.color,
                                  fontWeight: 'bold'
                                }}
                                onClick={() => handleBulkStatusChange(requestId, status.id)}
                              >
                                Toate → {status.label}
                              </div>
                            )
                          }))
                        }}
                        trigger={['click']}
                        placement="bottomLeft"
                      >
                        <Button 
                          size="small" 
                          type="dashed"
                          style={{ 
                            borderColor: '#1890ff',
                            color: '#1890ff'
                          }}
                        >
                          Status <DownOutlined />
                        </Button>
                      </Dropdown>
                      {/* Buton pentru PDF individual pe mobile */}
                      {user?.role_id !== 5 && (
                        <FurnizorPDFGenerator 
                          furnizorData={furnizorData}
                          groupedProducts={{ [requestId]: products }}
                          productRequestStatuses={productRequestStatuses}
                          calculateRequestTotal={calculateRequestTotal}
                          furnizor_id={furnizor_id}
                          isSingleRequest={true}
                          requestId={requestId}
                        />
                      )}
                    </Space>
                  </Space>
                </div>
              }
              extra={
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => setSelectedRequest(selectedRequest === requestId ? null : requestId)}
                  className="md:hidden"
                >
                  {selectedRequest === requestId ? 'Ascunde' : 'Detalii'}
                </Button>
              }
              className="md:hidden"
            >
              {selectedRequest === requestId && (
                <div>
                  <Divider style={{ margin: '16px 0' }} />
                  <Row gutter={[16, 16]}>
                    {products.map((product) => {
                      const status = getProductStatus(product.status_id);
                      const productTotal = product.produs_oferta.bought_price * product.produs_oferta.product_qt;
                      
                      // Creez meniul pentru dropdown cu statusuri
                      const statusMenuItems = getAvailableStatuses().map(statusOption => {
                        const isCurrentStatus = statusOption.id === product.status_id;
                        return {
                          key: statusOption.id,
                          label: (
                            <div 
                              style={{ 
                                padding: '8px 12px',
                                backgroundColor: isCurrentStatus ? statusOption.color : 'transparent',
                                color: isCurrentStatus ? '#fff' : statusOption.color,
                                fontWeight: isCurrentStatus ? 'bold' : 'normal',
                                borderRadius: '4px'
                              }}
                              onClick={() => !isCurrentStatus && handleStatusChange(product.id, statusOption.id)}
                            >
                              {statusOption.label}
                              {isCurrentStatus && ' (actual)'}
                            </div>
                          ),
                          disabled: isCurrentStatus
                        };
                      });
                      
                      return (
                        <Col span={24} key={product.id}>
                          <Card 
                            size="small"
                            style={{ 
                              borderLeft: `4px solid ${status?.color || '#d9d9d9'}`,
                              background: '#fafafa'
                            }}
                          >
                            {/* Mobile: Layout vertical */}
                            <div className="block md:hidden">
                              <div className="mb-3">
                                {product.produs.poza_principala_url && (
                                  <img 
                                    src={`${process.env.REACT_APP_API_URL}${product.produs.poza_principala_url}`}
                                    alt={product.produs.denumire}
                                    style={{ 
                                      width: '80px', 
                                      height: '80px', 
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      float: 'left',
                                      marginRight: '12px'
                                    }}
                                  />
                                )}
                                <div>
                                  <Title level={5} style={{ margin: 0, marginBottom: '4px', fontSize: '14px' }}>
                                    {product.produs.denumire}
                                  </Title>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>SKU: {product.produs.sku_intern}</Text>
                                </div>
                              </div>
                              <div style={{ clear: 'both' }}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <div>
                                    <Text><strong>Cantitate:</strong> {product.produs_oferta.product_qt} {product.produs.unit_of_measure}</Text>
                                  </div>
                                  <div>
                                    <Text><strong>Preț unitar:</strong> {product.produs_oferta.bought_price} MDL</Text>
                                  </div>
                                  <div>
                                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                      <strong>Total:</strong> {productTotal.toFixed(2)} MDL
                                    </Text>
                                  </div>
                                  <Dropdown
                                    menu={{ items: statusMenuItems }}
                                    trigger={['click']}
                                    placement="bottomLeft"
                                  >
                                    <Button 
                                      style={{ 
                                        borderColor: status?.color,
                                        color: status?.color,
                                        width: '100%',
                                        borderRadius: '6px',
                                        fontWeight: '500'
                                      }}
                                      size="small"
                                    >
                                      {status?.label || 'Necunoscut'} <DownOutlined />
                                    </Button>
                                  </Dropdown>
                                </Space>
                              </div>
                            </div>

                            {/* Desktop: Layout orizontal */}
                            <div className="hidden md:block">
                              <Row gutter={16} align="middle">
                                <Col span={2}>
                                  {product.produs.poza_principala_url && (
                                    <img 
                                      src={`${process.env.REACT_APP_API_URL}${product.produs.poza_principala_url}`}
                                      alt={product.produs.denumire}
                                      style={{ 
                                        width: '60px', 
                                        height: '60px', 
                                        objectFit: 'cover',
                                        borderRadius: '8px'
                                      }}
                                    />
                                  )}
                                </Col>
                                <Col span={10}>
                                  <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                                    {product.produs.denumire}
                                  </Title>
                                  <Text type="secondary">SKU: {product.produs.sku_intern}</Text>
                                  <br />
                                  <Text>
                                    <strong>Cantitate:</strong> {product.produs_oferta.product_qt} {product.produs.unit_of_measure}
                                  </Text>
                                </Col>
                                <Col span={6}>
                                  <Space direction="vertical" size="small">
                                    <Text>
                                      <strong>Preț unitar:</strong> {product.produs_oferta.bought_price} MDL
                                    </Text>
                                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                      <strong>Total:</strong> {productTotal.toFixed(2)} MDL
                                    </Text>
                                  </Space>
                                </Col>
                                <Col span={6}>
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Dropdown
                                      menu={{ items: statusMenuItems }}
                                      trigger={['click']}
                                      placement="bottomRight"
                                    >
                                      <Button 
                                        style={{ 
                                          borderColor: status?.color,
                                          color: status?.color,
                                          width: '100%',
                                          borderRadius: '20px',
                                          fontWeight: '500'
                                        }}
                                      >
                                        {status?.label || 'Necunoscut'} <DownOutlined />
                                      </Button>
                                    </Dropdown>
                                  </Space>
                                </Col>
                              </Row>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
            </Card>
            
            <Card
              key={`${requestId}-desktop`}
              style={{ 
                marginBottom: '16px', 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: hasUrgentStatus ? '2px solid #f59e0b' : '1px solid #d9d9d9'
              }}
              title={
                // Desktop: Layout orizontal
                <div className="hidden md:block">
                  <Row justify="space-between" align="top">
                    <Col span={6}>
                      <Space direction="vertical" size="small">
                        <Text strong className='text-wrap' style={{ fontSize: '18px' }}>
                          {products[0]?.client_name || `Cererea #${requestId}`}
                        </Text>
                        <Space>
                          <Badge 
                            count={products.length} 
                            style={{ backgroundColor: '#1890ff' }}
                            title="Numărul de produse"
                          />
                          <Tag color="blue" icon={<DollarOutlined />}>
                            Total: {total.toFixed(2)} MDL
                          </Tag>
                          <Dropdown
                            menu={{
                              items: getAvailableStatuses().map(status => ({
                                key: status.id,
                                label: (
                                  <div 
                                    style={{ 
                                      padding: '8px 12px',
                                      color: status.color,
                                      fontWeight: 'bold'
                                    }}
                                    onClick={() => handleBulkStatusChange(requestId, status.id)}
                                  >
                                    Toate → {status.label}
                                  </div>
                                )
                              }))
                            }}
                            trigger={['click']}
                            placement="bottomLeft"
                          >
                            <Button 
                              size="small" 
                              type="dashed"
                              style={{ 
                                borderColor: '#1890ff',
                                color: '#1890ff'
                              }}
                            >
                              Schimbă toate <DownOutlined />
                            </Button>
                          </Dropdown>
                          {/* Buton pentru PDF individual */}
                          {user?.role_id !== 5 && (
                            <FurnizorPDFGenerator 
                              furnizorData={furnizorData}
                              groupedProducts={{ [requestId]: products }}
                              productRequestStatuses={productRequestStatuses}
                              calculateRequestTotal={calculateRequestTotal}
                              furnizor_id={furnizor_id}
                              isSingleRequest={true}
                              requestId={requestId}
                            />
                          )}
                        </Space>
                      </Space>
                    </Col>
                    <Col span={16}>
                      <RequestSummary summary={summary} />
                    </Col>
                  </Row>
                </div>
              }
              extra={
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => setSelectedRequest(selectedRequest === requestId ? null : requestId)}
                  className="hidden md:block"
                >
                  {selectedRequest === requestId ? 'Ascunde' : 'Detalii'}
                </Button>
              }
              className="hidden md:block"
            >
              {selectedRequest === requestId && (
                <div>
                  <Divider style={{ margin: '16px 0' }} />
                  <Row gutter={[16, 16]}>
                    {products.map((product) => {
                      const status = getProductStatus(product.status_id);
                      const productTotal = product.produs_oferta.bought_price * product.produs_oferta.product_qt;
                      
                      // Creez meniul pentru dropdown cu statusuri
                      const statusMenuItems = getAvailableStatuses().map(statusOption => {
                        const isCurrentStatus = statusOption.id === product.status_id;
                        return {
                          key: statusOption.id,
                          label: (
                            <div 
                              style={{ 
                                padding: '8px 12px',
                                backgroundColor: isCurrentStatus ? statusOption.color : 'transparent',
                                color: isCurrentStatus ? '#fff' : statusOption.color,
                                fontWeight: isCurrentStatus ? 'bold' : 'normal',
                                borderRadius: '4px'
                              }}
                              onClick={() => !isCurrentStatus && handleStatusChange(product.id, statusOption.id)}
                            >
                              {statusOption.label}
                              {isCurrentStatus && ' (actual)'}
                            </div>
                          ),
                          disabled: isCurrentStatus
                        };
                      });
                      
                      return (
                        <Col span={24} key={product.id}>
                          <Card 
                            size="small"
                            style={{ 
                              borderLeft: `4px solid ${status?.color || '#d9d9d9'}`,
                              background: '#fafafa'
                            }}
                          >
                            {/* Mobile: Layout vertical */}
                            <div className="block md:hidden">
                              <div className="mb-3">
                                {product.produs.poza_principala_url && (
                                  <img 
                                    src={`${process.env.REACT_APP_API_URL}${product.produs.poza_principala_url}`}
                                    alt={product.produs.denumire}
                                    style={{ 
                                      width: '80px', 
                                      height: '80px', 
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      float: 'left',
                                      marginRight: '12px'
                                    }}
                                  />
                                )}
                                <div>
                                  <Title level={5} style={{ margin: 0, marginBottom: '4px', fontSize: '14px' }}>
                                    {product.produs.denumire}
                                  </Title>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>SKU: {product.produs.sku_intern}</Text>
                                </div>
                              </div>
                              <div style={{ clear: 'both' }}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <div>
                                    <Text><strong>Cantitate:</strong> {product.produs_oferta.product_qt} {product.produs.unit_of_measure}</Text>
                                  </div>
                                  <div>
                                    <Text><strong>Preț unitar:</strong> {product.produs_oferta.bought_price} MDL</Text>
                                  </div>
                                  <div>
                                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                      <strong>Total:</strong> {productTotal.toFixed(2)} MDL
                                    </Text>
                                  </div>
                                  <Dropdown
                                    menu={{ items: statusMenuItems }}
                                    trigger={['click']}
                                    placement="bottomLeft"
                                  >
                                    <Button 
                                      style={{ 
                                        borderColor: status?.color,
                                        color: status?.color,
                                        width: '100%',
                                        borderRadius: '6px',
                                        fontWeight: '500'
                                      }}
                                      size="small"
                                    >
                                      {status?.label || 'Necunoscut'} <DownOutlined />
                                    </Button>
                                  </Dropdown>
                                </Space>
                              </div>
                            </div>

                            {/* Desktop: Layout orizontal */}
                            <div className="hidden md:block">
                              <Row gutter={16} align="middle">
                                <Col span={2}>
                                  {product.produs.poza_principala_url && (
                                    <img 
                                      src={`${process.env.REACT_APP_API_URL}${product.produs.poza_principala_url}`}
                                      alt={product.produs.denumire}
                                      style={{ 
                                        width: '60px', 
                                        height: '60px', 
                                        objectFit: 'cover',
                                        borderRadius: '8px'
                                      }}
                                    />
                                  )}
                                </Col>
                                <Col span={10}>
                                  <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                                    {product.produs.denumire}
                                  </Title>
                                  <Text type="secondary">SKU: {product.produs.sku_intern}</Text>
                                  <br />
                                  <Text>
                                    <strong>Cantitate:</strong> {product.produs_oferta.product_qt} {product.produs.unit_of_measure}
                                  </Text>
                                </Col>
                                <Col span={6}>
                                  <Space direction="vertical" size="small">
                                    <Text>
                                      <strong>Preț unitar:</strong> {product.produs_oferta.bought_price} MDL
                                    </Text>
                                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                      <strong>Total:</strong> {productTotal.toFixed(2)} MDL
                                    </Text>
                                  </Space>
                                </Col>
                                <Col span={6}>
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Dropdown
                                      menu={{ items: statusMenuItems }}
                                      trigger={['click']}
                                      placement="bottomRight"
                                    >
                                      <Button 
                                        style={{ 
                                          borderColor: status?.color,
                                          color: status?.color,
                                          width: '100%',
                                          borderRadius: '20px',
                                          fontWeight: '500'
                                        }}
                                      >
                                        {status?.label || 'Necunoscut'} <DownOutlined />
                                      </Button>
                                    </Dropdown>
                                  </Space>
                                </Col>
                              </Row>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
            </Card>
          </>
        );
      })}
    </div>
  )
}

export default FurnizorPage